import type { Product, ProductVariant, Image as ShopifyImage } from '@/lib/shopify/types';

export type CustomizerSide = 'front' | 'back';
export type ArtworkKind = '' | 'image' | 'file';

export interface PrintAreaGeometry {
  label: string;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
}

export const TEXT_FONT_OPTIONS = [
  {
    id: 'sans',
    label: 'Sans',
    css: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  {
    id: 'serif',
    label: 'Serif',
    css: 'Georgia, "Times New Roman", serif',
  },
  {
    id: 'script',
    label: 'Script',
    css: '"Brush Script MT", "Segoe Script", cursive',
  },
  {
    id: 'mono',
    label: 'Mono',
    css: '"Courier New", Courier, monospace',
  },
  {
    id: 'impact',
    label: 'Impact',
    css: 'Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif',
  },
] as const;

export type TextFontId = (typeof TEXT_FONT_OPTIONS)[number]['id'];

export const TEXT_ALIGN_OPTIONS = [
  { id: 'left', label: 'Left' },
  { id: 'center', label: 'Centre' },
  { id: 'right', label: 'Right' },
] as const;

export type TextAlignId = (typeof TEXT_ALIGN_OPTIONS)[number]['id'];

export interface PrintAreaProfile extends PrintAreaGeometry {
  id: string;
  frontLabel: string;
  backLabel: string;
  supportsBack: boolean;
  sideOverrides?: Partial<Record<CustomizerSide, Partial<PrintAreaGeometry>>>;
}

export interface SideDesign {
  artworkUrl: string;
  artworkFilename: string;
  artworkKind: ArtworkKind;
  artworkWarnings: string[];
  x: number;
  y: number;
  scale: number;
  rotation: number;
  text: string;
  textX: number;
  textY: number;
  textRotation: number;
  textSize: number;
  textColor: string;
  textFont: TextFontId;
  textBold: boolean;
  textItalic: boolean;
  textAlign: TextAlignId;
}

export interface CustomDesignState {
  activeSide: CustomizerSide;
  profile: PrintAreaProfile;
  sides: Record<CustomizerSide, SideDesign>;
  instructions: string;
}

export interface CartAttribute {
  key: string;
  value: string;
}

const DEFAULT_SIDE_DESIGN: SideDesign = {
  artworkUrl: '',
  artworkFilename: '',
  artworkKind: '',
  artworkWarnings: [],
  x: 50,
  y: 48,
  scale: 58,
  rotation: 0,
  text: '',
  textX: 50,
  textY: 50,
  textRotation: 0,
  textSize: 24,
  textColor: '#111111',
  textFont: 'sans',
  textBold: true,
  textItalic: false,
  textAlign: 'center',
};

export const DEFAULT_PRINT_PROFILE: PrintAreaProfile = {
  id: 'classic-garment',
  label: 'Chest print area',
  frontLabel: 'Front',
  backLabel: 'Back',
  supportsBack: true,
  width: 44,
  height: 42,
  centerX: 50,
  centerY: 42,
};

function createPrintProfile(
  id: string,
  label: string,
  width: number,
  height: number,
  centerX: number,
  centerY: number,
  frontLabel = 'Front',
  backLabel = 'Back',
  supportsBack = true,
  sideOverrides?: PrintAreaProfile['sideOverrides']
): PrintAreaProfile {
  return {
    id,
    label,
    frontLabel,
    backLabel,
    supportsBack,
    width,
    height,
    centerX,
    centerY,
    sideOverrides,
  };
}

export function getPrintAreaForSide(profile: PrintAreaProfile, side: CustomizerSide): PrintAreaGeometry {
  const override = profile.sideOverrides?.[side];
  return {
    label: override?.label ?? profile.label,
    width: override?.width ?? profile.width,
    height: override?.height ?? profile.height,
    centerX: override?.centerX ?? profile.centerX,
    centerY: override?.centerY ?? profile.centerY,
  };
}

const CAP_BACK_STRAP_AREA: Partial<PrintAreaGeometry> = {
  label: 'Back strap embroidery area',
  width: 34,
  height: 16,
  centerX: 50,
  centerY: 50,
};

const APRON_LOGO_AREA: Partial<PrintAreaGeometry> = {
  label: 'Apron logo area',
  width: 18,
  height: 9,
  centerX: 50,
  centerY: 30,
};

const CAP_FRONT_LOGO_AREA: Partial<PrintAreaGeometry> = {
  label: 'Cap logo area',
  width: 28,
  height: 16,
  centerX: 50,
  centerY: 38,
};

const BAG_LOGO_AREA: Partial<PrintAreaGeometry> = {
  label: 'Bag logo area',
  width: 36,
  height: 28,
  centerX: 50,
  centerY: 54,
};

const PRINT_PROFILE_OVERRIDES: Record<string, PrintAreaProfile> = {
  'custom-printed-round-neck-t-shirt': createPrintProfile('round-neck-chest', 'Chest print area', 24, 22, 59, 31),
  'custom-embroidered-polo-t-shirt': createPrintProfile('polo-left-chest', 'Polo chest area', 38, 34, 50, 40),
  'custom-printed-long-sleeve-t-shirt': createPrintProfile('long-sleeve-chest', 'Chest print area', 24, 22, 59, 30),
  'custom-printed-baby-bodysuits': createPrintProfile('baby-bodysuit-chest', 'Baby bodysuit print area', 40, 36, 50, 45),
  'custom-printed-kids-t-shirt': createPrintProfile('kids-tshirt-chest', 'Kids chest print area', 42, 40, 50, 43),
  'custom-embroidered-kids-polo-shirt': createPrintProfile('kids-polo-chest', 'Kids polo chest area', 36, 32, 50, 40),
  'custom-embroidered-kids-polo-3-5y': createPrintProfile('kids-polo-3-5-chest', 'Kids polo chest area', 34, 30, 50, 40),
  'custom-embroidered-kids-polo-5-11y': createPrintProfile('kids-polo-5-11-chest', 'Kids polo chest area', 36, 32, 50, 40),
  'custom-embroidered-kids-polo-12-14y': createPrintProfile('kids-polo-12-14-chest', 'Kids polo chest area', 38, 34, 50, 40),
  'custom-printed-school-pe-t-shirt': createPrintProfile('school-pe-tshirt-chest', 'Chest print area', 42, 40, 50, 43),
  'custom-printed-sports-t-shirt': createPrintProfile('sports-tshirt-chest', 'Chest print area', 30, 22, 64, 31),
  'custom-embroidered-sports-polo-shirt': createPrintProfile('sports-polo-chest', 'Polo chest area', 38, 34, 50, 40),
  'custom-embroidered-school-cardigan': createPrintProfile('school-cardigan-chest', 'Cardigan chest area', 38, 34, 50, 40),

  'custom-embroidered-baseball-cap': createPrintProfile('baseball-cap-front-panel', 'Cap front panel', 42, 22, 50, 38, 'Front panel', 'Back strap', true, { back: CAP_BACK_STRAP_AREA }),
  'custom-embroidered-snapback-cap': createPrintProfile('snapback-front-panel', 'Cap front panel', 44, 22, 50, 38, 'Front panel', 'Back strap', true, { back: CAP_BACK_STRAP_AREA }),
  'custom-embroidered-kids-baseball-caps': createPrintProfile('kids-cap-logo-area', 'Cap logo area', 28, 16, 50, 38, 'Front panel', 'Back strap', true, { back: CAP_BACK_STRAP_AREA }),
  'custom-embroidered-5-panel-snapback': createPrintProfile('five-panel-cap-front', 'Cap front panel', 46, 24, 50, 38, 'Front panel', 'Back strap', true, { back: CAP_BACK_STRAP_AREA }),
  'custom-embroidered-chef-hat': createPrintProfile('chef-hat-logo-area', 'Chef hat logo area', 20, 16, 49, 50, 'Front panel', 'Back'),

  'custom-printed-school-pe-shorts': createPrintProfile('school-shorts-leg', 'Shorts leg print area', 34, 46, 50, 52, 'Left leg', 'Right leg'),
  'custom-printed-baggy-joggers': createPrintProfile('baggy-joggers-leg', 'Jogger leg print area', 30, 58, 50, 52, 'Left leg', 'Right leg'),
  'custom-printed-zipped-joggers': createPrintProfile('zipped-joggers-leg', 'Jogger leg print area', 30, 58, 50, 52, 'Left leg', 'Right leg'),
  'custom-printed-workout-leggings': createPrintProfile('leggings-leg', 'Legging print area', 28, 60, 50, 52, 'Left leg', 'Right leg'),

  'custom-printed-sweatshirt': createPrintProfile('sweatshirt-chest', 'Sweatshirt print area', 24, 22, 63, 31),
  'custom-printed-hoodie-80-20': createPrintProfile('hoodie-chest', 'Hoodie print area', 26, 24, 62, 40),
  'custom-printed-oversized-hoodie': createPrintProfile('oversized-hoodie-chest', 'Hoodie print area', 28, 24, 62, 40),
  'custom-embroidered-school-sweatshirt': createPrintProfile('school-sweatshirt-chest', 'Sweatshirt chest area', 40, 36, 50, 40),
  'custom-embroidered-softshell-jacket': createPrintProfile('softshell-jacket-chest', 'Jacket chest area', 38, 34, 50, 39),
  'custom-embroidered-full-zip-fleece': createPrintProfile('fleece-chest', 'Fleece chest area', 38, 34, 50, 39),
  'custom-embroidered-bodywarmer-gilet': createPrintProfile('bodywarmer-chest', 'Bodywarmer chest area', 38, 34, 50, 39),
  'custom-embroidered-two-tone-gilet': createPrintProfile('two-tone-gilet-chest', 'Gilet chest area', 38, 34, 50, 39),
  'custom-embroidered-hi-vis-zip-hoodie': createPrintProfile('hi-vis-hoodie-chest', 'Hi-vis chest area', 40, 36, 50, 40),

  'custom-embroidered-chef-jacket': createPrintProfile('chef-jacket-chest', 'Chef jacket chest area', 38, 34, 50, 40),
  'custom-embroidered-apron-with-pocket': createPrintProfile('apron-logo-area', 'Apron logo area', 18, 9, 50, 30),
  'custom-embroidered-cotton-apron': createPrintProfile('cotton-apron-logo-area', 'Apron logo area', 18, 9, 50, 30),
  'custom-embroidered-beauty-tunic': createPrintProfile('beauty-tunic-chest', 'Tunic chest area', 38, 36, 50, 40),

  'custom-embroidered-large-towel': createPrintProfile('large-towel-panel', 'Towel print panel', 58, 38, 50, 50),
  'custom-embroidered-medium-towel': createPrintProfile('medium-towel-panel', 'Towel print panel', 58, 38, 50, 50),

  'custom-printed-tote-bag': createPrintProfile('tote-bag-logo-area', 'Tote bag logo area', 36, 26, 50, 58),
  'custom-printed-school-book-bag': createPrintProfile('school-book-bag-logo-area', 'Book bag logo area', 36, 28, 50, 52),
  'custom-printed-school-pe-bag': createPrintProfile('drawstring-bag-logo-area', 'Drawstring bag logo area', 34, 24, 50, 48),
  'custom-printed-book-bag-with-strap': createPrintProfile('strap-book-bag-logo-area', 'Book bag logo area', 36, 28, 50, 52),
};

export interface PrintProfileSelectionContext {
  variant?: Pick<ProductVariant, 'title' | 'selectedOptions'> | null;
  image?: Pick<ShopifyImage, 'url' | 'altText'> | null;
  side?: CustomizerSide;
}

type PrintProfilePatch = Partial<Omit<PrintAreaProfile, 'sideOverrides'>> & {
  sideOverrides?: PrintAreaProfile['sideOverrides'];
};

interface PrintProfileSelectionRule {
  handles?: string[];
  productTokens?: string[];
  optionTokens?: string[];
  imageTokens?: string[];
  side?: CustomizerSide;
  idSuffix?: string;
  patch: PrintProfilePatch;
}

const LEFT_CHEST_EMBROIDERY_AREA: PrintProfilePatch = {
  label: 'Left chest embroidery area',
  width: 40,
  height: 36,
  centerX: 74,
  centerY: 39,
};

const TUNIC_LOGO_AREA: PrintProfilePatch = {
  label: 'Tunic logo area',
  width: 34,
  height: 24,
  centerX: 40,
  centerY: 38,
};

const CHEF_JACKET_LOGO_AREA: PrintProfilePatch = {
  label: 'Left chest embroidery area',
  width: 28,
  height: 24,
  centerX: 67,
  centerY: 33,
};

const KIDS_LEFT_CHEST_EMBROIDERY_AREA: PrintProfilePatch = {
  label: 'Kids left chest embroidery area',
  width: 24,
  height: 22,
  centerX: 60,
  centerY: 40,
};

const PRINT_PROFILE_SELECTION_RULES: PrintProfileSelectionRule[] = [
  {
    handles: ['custom-embroidered-beauty-tunic'],
    side: 'front',
    idSuffix: 'tunic-logo',
    patch: TUNIC_LOGO_AREA,
  },
  {
    handles: [
      'custom-embroidered-polo-t-shirt',
      'custom-embroidered-sports-polo-shirt',
      'custom-embroidered-school-cardigan',
      'custom-embroidered-school-sweatshirt',
      'custom-embroidered-softshell-jacket',
      'custom-embroidered-full-zip-fleece',
      'custom-embroidered-bodywarmer-gilet',
      'custom-embroidered-two-tone-gilet',
      'custom-embroidered-hi-vis-zip-hoodie',
    ],
    side: 'front',
    idSuffix: 'left-chest',
    patch: LEFT_CHEST_EMBROIDERY_AREA,
  },
  {
    handles: ['custom-embroidered-softshell-jacket'],
    side: 'front',
    idSuffix: 'softshell-left-chest',
    patch: { width: 24, height: 22, centerX: 62, centerY: 36 },
  },
  {
    handles: ['custom-embroidered-full-zip-fleece'],
    side: 'front',
    idSuffix: 'fleece-left-chest',
    patch: { width: 24, height: 22, centerX: 62, centerY: 37 },
  },
  {
    handles: ['custom-embroidered-bodywarmer-gilet'],
    side: 'front',
    idSuffix: 'bodywarmer-left-chest',
    patch: { width: 23, height: 21, centerX: 60, centerY: 35 },
  },
  {
    handles: ['custom-embroidered-two-tone-gilet'],
    side: 'front',
    idSuffix: 'two-tone-gilet-left-chest',
    patch: { width: 23, height: 21, centerX: 59, centerY: 40 },
  },
  {
    handles: [
      'custom-embroidered-polo-t-shirt',
      'custom-embroidered-sports-polo-shirt',
    ],
    side: 'front',
    idSuffix: 'polo-left-chest',
    patch: { width: 24, height: 22, centerX: 62, centerY: 32 },
  },
  {
    handles: ['custom-embroidered-chef-jacket'],
    side: 'front',
    idSuffix: 'chef-jacket-logo',
    patch: CHEF_JACKET_LOGO_AREA,
  },
  {
    handles: [
      'custom-embroidered-kids-polo-shirt',
      'custom-embroidered-kids-polo-3-5y',
      'custom-embroidered-kids-polo-5-11y',
      'custom-embroidered-kids-polo-12-14y',
    ],
    side: 'front',
    idSuffix: 'kids-left-chest',
    patch: KIDS_LEFT_CHEST_EMBROIDERY_AREA,
  },
  {
    productTokens: ['embroider'],
    optionTokens: ['left', 'chest'],
    side: 'front',
    idSuffix: 'left-chest',
    patch: LEFT_CHEST_EMBROIDERY_AREA,
  },
  {
    productTokens: ['embroider'],
    optionTokens: ['front'],
    imageTokens: ['left', 'chest'],
    side: 'front',
    idSuffix: 'left-chest',
    patch: LEFT_CHEST_EMBROIDERY_AREA,
  },
  {
    productTokens: ['cap'],
    side: 'back',
    idSuffix: 'back-strap',
    patch: CAP_BACK_STRAP_AREA,
  },
  {
    productTokens: ['hat'],
    side: 'back',
    idSuffix: 'back-strap',
    patch: CAP_BACK_STRAP_AREA,
  },
  {
    productTokens: ['cap'],
    side: 'front',
    idSuffix: 'logo-area',
    patch: CAP_FRONT_LOGO_AREA,
  },
  {
    handles: ['custom-embroidered-5-panel-snapback'],
    side: 'front',
    idSuffix: 'front-panel-lower',
    patch: { centerY: 43 },
  },
  {
    handles: ['custom-embroidered-baseball-cap'],
    side: 'front',
    idSuffix: 'front-panel-higher',
    patch: { centerY: 34 },
  },
  {
    handles: ['custom-embroidered-snapback-cap'],
    side: 'front',
    idSuffix: 'larger-front-panel',
    patch: { width: 34, height: 22, centerY: 38 },
  },
  {
    productTokens: ['apron'],
    optionTokens: ['pocket'],
    side: 'front',
    idSuffix: 'pocket',
    patch: APRON_LOGO_AREA,
  },
  {
    productTokens: ['bag'],
    imageTokens: ['back'],
    side: 'back',
    idSuffix: 'back-panel',
    patch: {
      label: 'Back bag logo area',
      width: BAG_LOGO_AREA.width,
      height: BAG_LOGO_AREA.height,
      centerX: BAG_LOGO_AREA.centerX,
      centerY: BAG_LOGO_AREA.centerY,
    },
  },
  {
    handles: ['custom-printed-tote-bag'],
    optionTokens: ['natural', 'cream'],
    idSuffix: 'natural-cream',
    patch: {
      centerY: 62,
    },
  },
];

function tokenizePrintProfileText(value: string): string[] {
  return value.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}

function textMatchesTokens(text: string, tokens?: string[]): boolean {
  if (!tokens || tokens.length === 0) return true;
  const haystack = tokenizePrintProfileText(text);
  return tokens.every((token) => haystack.some((part) => part.includes(token.toLowerCase())));
}

function getVariantProfileText(variant?: PrintProfileSelectionContext['variant']): string {
  if (!variant) return '';
  return [
    variant.title,
    ...variant.selectedOptions.flatMap((option) => [option.name, option.value]),
  ].join(' ');
}

function getImageProfileText(image?: PrintProfileSelectionContext['image']): string {
  if (!image) return '';
  const filename = decodeURIComponent(image.url.split('/').pop()?.split('?')[0] || '');
  return `${image.altText || ''} ${filename}`;
}

function applyPrintProfilePatch(
  profile: PrintAreaProfile,
  patch: PrintProfilePatch,
  idSuffix?: string
): PrintAreaProfile {
  const sideOverrides = profile.sideOverrides || patch.sideOverrides
    ? {
        ...(profile.sideOverrides || {}),
        ...(patch.sideOverrides || {}),
      }
    : undefined;

  return {
    ...profile,
    ...patch,
    id: patch.id || (idSuffix ? `${profile.id}-${idSuffix}` : profile.id),
    sideOverrides,
  };
}

function printProfileRuleMatches(
  rule: PrintProfileSelectionRule,
  product: Pick<Product, 'handle' | 'title' | 'productType' | 'tags'>,
  context: PrintProfileSelectionContext,
  productText: string,
  variantText: string,
  imageText: string
): boolean {
  if (rule.handles && !rule.handles.includes(product.handle)) return false;
  if (rule.side && rule.side !== (context.side || 'front')) return false;
  if (!textMatchesTokens(productText, rule.productTokens)) return false;
  if (!textMatchesTokens(variantText, rule.optionTokens)) return false;
  if (!textMatchesTokens(imageText, rule.imageTokens)) return false;
  return true;
}

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'svg', 'webp']);
const ARTWORK_EXTENSION_LABELS: Record<string, string> = {
  ai: 'AI print file',
  eps: 'EPS print file',
  jpg: 'JPG image',
  jpeg: 'JPEG image',
  pdf: 'PDF print file',
  png: 'PNG image',
  svg: 'SVG image',
  webp: 'WEBP image',
};

export function isImageArtwork(filename: string): boolean {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return IMAGE_EXTENSIONS.has(ext);
}

export function getArtworkExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || '';
}

export function getArtworkKindLabel(side: Pick<SideDesign, 'artworkFilename' | 'artworkKind'>): string {
  const extension = getArtworkExtension(side.artworkFilename);
  if (extension && ARTWORK_EXTENSION_LABELS[extension]) return ARTWORK_EXTENSION_LABELS[extension];
  return side.artworkKind === 'image' ? 'Image artwork' : 'Print file';
}

export function createDefaultCustomDesign(profile: PrintAreaProfile = DEFAULT_PRINT_PROFILE): CustomDesignState {
  return {
    activeSide: 'front',
    profile,
    sides: {
      front: { ...DEFAULT_SIDE_DESIGN },
      back: { ...DEFAULT_SIDE_DESIGN },
    },
    instructions: '',
  };
}

export function productHasCustomizerPreviewImage(product: Pick<Product, 'featuredImage' | 'images' | 'variants'>): boolean {
  return Boolean(
    product.featuredImage?.url ||
    product.images.some((image) => image.url) ||
    product.variants.some((variant) => variant.image?.url)
  );
}

export const CUSTOMIZER_PENDING_IMAGE_PRODUCT_HANDLES = new Set([
  'custom-embroidered-large-towel',
  'custom-embroidered-medium-towel',
  'custom-embroidered-hi-vis-zip-hoodie',
  'custom-embroidered-kids-polo-3-5y',
  'custom-embroidered-kids-polo-5-11y',
  'custom-embroidered-kids-polo-12-14y',
  'custom-embroidered-kids-polo-shirt',
  'custom-embroidered-school-cardigan',
  'custom-embroidered-school-sweatshirt',
  'custom-printed-kids-t-shirt',
  'custom-printed-school-pe-t-shirt',
  'custom-printed-baggy-joggers',
  'custom-printed-zipped-joggers',
  'custom-printed-workout-leggings',
  'custom-printed-school-pe-shorts',
]);

export function productHasReadyCustomizerPreviewImage(
  product: Pick<Product, 'handle' | 'featuredImage' | 'images' | 'variants'>
): boolean {
  return productHasCustomizerPreviewImage(product) && !CUSTOMIZER_PENDING_IMAGE_PRODUCT_HANDLES.has(product.handle);
}

export function getProductPrintProfile(product: Pick<Product, 'handle' | 'title' | 'productType' | 'tags'>): PrintAreaProfile {
  const override = PRINT_PROFILE_OVERRIDES[product.handle];
  if (override) return override;

  const haystack = `${product.productType} ${product.title} ${product.tags.join(' ')}`.toLowerCase();

  if (haystack.includes('sticker') || haystack.includes('label')) {
    return {
      id: 'sticker',
      label: 'Label face',
      frontLabel: 'Front',
      backLabel: 'Reverse',
      supportsBack: false,
      width: 70,
      height: 48,
      centerX: 50,
      centerY: 50,
    };
  }

  if (haystack.includes('banner')) {
    return {
      id: 'banner',
      label: 'Banner face',
      frontLabel: 'Front',
      backLabel: 'Reverse',
      supportsBack: false,
      width: 76,
      height: 58,
      centerX: 50,
      centerY: 50,
    };
  }

  if (
    haystack.includes('trouser') ||
    haystack.includes('jogger') ||
    haystack.includes('legging') ||
    haystack.includes('shorts')
  ) {
    return {
      id: 'leg-print',
      label: 'Leg print area',
      frontLabel: 'Front / left leg',
      backLabel: 'Back / right leg',
      supportsBack: true,
      width: 30,
      height: 56,
      centerX: 50,
      centerY: 50,
    };
  }

  if (haystack.includes('cap') || haystack.includes('hat')) {
    return {
      id: 'cap-logo-area',
      label: 'Cap logo area',
      frontLabel: 'Front panel',
      backLabel: 'Back strap',
      supportsBack: true,
      width: 28,
      height: 16,
      centerX: 50,
      centerY: 38,
      sideOverrides: { back: CAP_BACK_STRAP_AREA },
    };
  }

  if (/\bbags?\b/.test(haystack)) {
    return {
      id: 'bag-logo-area',
      label: 'Bag logo area',
      frontLabel: 'Front',
      backLabel: 'Back',
      supportsBack: true,
      width: 36,
      height: 28,
      centerX: BAG_LOGO_AREA.centerX ?? 50,
      centerY: BAG_LOGO_AREA.centerY ?? 54,
    };
  }

  if (haystack.includes('towel')) {
    return {
      id: 'towel-panel',
      label: 'Towel print panel',
      frontLabel: 'Front',
      backLabel: 'Back',
      supportsBack: true,
      width: 58,
      height: 38,
      centerX: 50,
      centerY: 50,
    };
  }

  if (haystack.includes('apron')) {
    return {
      id: 'apron-logo-area',
      label: 'Apron logo area',
      frontLabel: 'Front',
      backLabel: 'Back',
      supportsBack: true,
      width: 18,
      height: 9,
      centerX: 50,
      centerY: 30,
    };
  }

  if (
    haystack.includes('tunic') ||
    haystack.includes('chef') ||
    haystack.includes('workwear')
  ) {
    return {
      id: 'workwear-chest',
      label: 'Workwear print area',
      frontLabel: 'Front',
      backLabel: 'Back',
      supportsBack: true,
      width: 40,
      height: 40,
      centerX: 50,
      centerY: 42,
    };
  }

  if (
    haystack.includes('hoodie') ||
    haystack.includes('sweatshirt') ||
    haystack.includes('jacket') ||
    haystack.includes('fleece') ||
    haystack.includes('gilet')
  ) {
    return {
      id: 'outerwear-chest',
      label: 'Garment print area',
      frontLabel: 'Front',
      backLabel: 'Back',
      supportsBack: true,
      width: 42,
      height: 38,
      centerX: 50,
      centerY: 40,
    };
  }

  return DEFAULT_PRINT_PROFILE;
}

export function getSelectedProductPrintProfile(
  product: Pick<Product, 'handle' | 'title' | 'productType' | 'tags'>,
  context: PrintProfileSelectionContext = {}
): PrintAreaProfile {
  const productText = `${product.handle} ${product.productType} ${product.title} ${product.tags.join(' ')}`;
  const variantText = getVariantProfileText(context.variant);
  const imageText = getImageProfileText(context.image);

  return PRINT_PROFILE_SELECTION_RULES.reduce((profile, rule) => {
    if (!printProfileRuleMatches(rule, product, context, productText, variantText, imageText)) return profile;
    return applyPrintProfilePatch(profile, rule.patch, rule.idSuffix);
  }, getProductPrintProfile(product));
}

export function printProfilesEqual(a: PrintAreaProfile, b: PrintAreaProfile): boolean {
  return (
    a.id === b.id &&
    a.label === b.label &&
    a.frontLabel === b.frontLabel &&
    a.backLabel === b.backLabel &&
    a.supportsBack === b.supportsBack &&
    a.width === b.width &&
    a.height === b.height &&
    a.centerX === b.centerX &&
    a.centerY === b.centerY &&
    JSON.stringify(a.sideOverrides || {}) === JSON.stringify(b.sideOverrides || {})
  );
}

export function getDesignSideLabel(design: CustomDesignState, side: CustomizerSide): string {
  return side === 'front' ? design.profile.frontLabel : design.profile.backLabel;
}

export function getTextFontOption(fontId: TextFontId = 'sans') {
  return TEXT_FONT_OPTIONS.find((font) => font.id === fontId) || TEXT_FONT_OPTIONS[0];
}

export function getTextAlignOption(alignId: TextAlignId = 'center') {
  return TEXT_ALIGN_OPTIONS.find((align) => align.id === alignId) || TEXT_ALIGN_OPTIONS[1];
}

export function sideHasDesign(side: SideDesign): boolean {
  return Boolean(side.artworkUrl || side.text.trim());
}

export function hasCustomDesign(design?: CustomDesignState | null): boolean {
  if (!design) return false;
  return sideHasDesign(design.sides.front) || (design.profile.supportsBack && sideHasDesign(design.sides.back)) || Boolean(design.instructions.trim());
}

export function getPrimaryArtworkUrl(design?: CustomDesignState | null): string {
  if (!design) return '';
  return design.sides.front.artworkUrl || design.sides.back.artworkUrl || '';
}

function trimAttribute(value: string, maxLength = 250): string {
  const compact = value.replace(/\s+/g, ' ').trim();
  return compact.length > maxLength ? `${compact.slice(0, maxLength - 3)}...` : compact;
}

function round(value: number): number {
  return Math.round(value);
}

function compactSideDesign(side: SideDesign): string {
  const parts = [
    `x=${round(side.x)}`,
    `y=${round(side.y)}`,
    `scale=${round(side.scale)}`,
    `rotate=${round(side.rotation)}`,
  ];
  if (side.artworkFilename) parts.push(`file=${side.artworkFilename}`);
  if (side.artworkUrl) parts.push(`kind=${getArtworkKindLabel(side)}`);
  if (side.artworkWarnings.length > 0) parts.push(`warnings=${side.artworkWarnings.join(', ')}`);
  if (side.text.trim()) {
    parts.push(`text=${side.text.trim()}`);
    parts.push(`textX=${round(side.textX)}`);
    parts.push(`textY=${round(side.textY)}`);
    parts.push(`textRotate=${round(side.textRotation)}`);
    parts.push(`font=${side.textFont}`);
    parts.push(`bold=${side.textBold ? 'yes' : 'no'}`);
    parts.push(`italic=${side.textItalic ? 'yes' : 'no'}`);
    parts.push(`align=${side.textAlign}`);
  }
  return trimAttribute(parts.join(';'));
}

export function getCustomizerCartAttributes(design?: CustomDesignState | null): CartAttribute[] {
  if (!design || !hasCustomDesign(design)) return [];

  const sides: CustomizerSide[] = design.profile.supportsBack ? ['front', 'back'] : ['front'];
  const designedSides = sides.filter((side) => sideHasDesign(design.sides[side]));
  const attributes: CartAttribute[] = [
    { key: '_customizerVersion', value: 'oktshirt-visual-customizer-v1' },
    { key: '_printProfile', value: design.profile.id },
  ];

  const artworkSides = designedSides.filter((side) => design.sides[side].artworkUrl);
  if (artworkSides.length === 1) {
    const side = artworkSides[0];
    attributes.push({ key: 'Artwork', value: design.sides[side].artworkUrl });
    if (design.sides[side].artworkFilename) {
      attributes.push({ key: 'Artwork file', value: trimAttribute(design.sides[side].artworkFilename) });
    }
    attributes.push({ key: 'Artwork type', value: getArtworkKindLabel(design.sides[side]) });
  } else if (artworkSides.length > 1) {
    artworkSides.forEach((side) => {
      const label = getDesignSideLabel(design, side).toLowerCase();
      attributes.push({ key: `Artwork ${label}`, value: design.sides[side].artworkUrl });
      if (design.sides[side].artworkFilename) {
        attributes.push({ key: `Artwork file ${label}`, value: trimAttribute(design.sides[side].artworkFilename) });
      }
      attributes.push({ key: `Artwork type ${label}`, value: getArtworkKindLabel(design.sides[side]) });
    });
  }

  if (designedSides.length > 0) {
    attributes.push({
      key: 'Design side',
      value: trimAttribute(designedSides.map((side) => getDesignSideLabel(design, side)).join(', ')),
    });

    const printAreaSummary = designedSides
      .map((side) => {
        const area = getPrintAreaForSide(design.profile, side);
        return `${getDesignSideLabel(design, side)}: ${area.label} ${round(area.width)}x${round(area.height)} at ${round(area.centerX)}%/${round(area.centerY)}%`;
      })
      .join('; ');
    attributes.push({ key: 'Design print area', value: trimAttribute(printAreaSummary) });

    const artworkWarningSummary = designedSides
      .filter((side) => design.sides[side].artworkWarnings.length > 0)
      .map((side) => `${getDesignSideLabel(design, side)}: ${design.sides[side].artworkWarnings.join(', ')}`)
      .join('; ');
    if (artworkWarningSummary) {
      attributes.push({ key: 'Artwork warning', value: trimAttribute(artworkWarningSummary) });
    }

    const placementSummary = designedSides
      .flatMap((side) => {
        const s = design.sides[side];
        const label = getDesignSideLabel(design, side);
        const parts: string[] = [];
        if (s.artworkUrl) {
          parts.push(`${label} artwork: ${round(s.x)}%/${round(s.y)}%, ${round(s.scale)}%, ${round(s.rotation)}deg`);
        }
        if (s.text.trim()) {
          parts.push(`${label} text: ${round(s.textX)}%/${round(s.textY)}%, ${round(s.textSize)}px, ${round(s.textRotation)}deg`);
        }
        return parts;
      })
      .join('; ');
    if (placementSummary) {
      attributes.push({
        key: 'Design placement',
        value: trimAttribute(placementSummary),
      });
    }

    const textSummary = designedSides
      .filter((side) => design.sides[side].text.trim())
      .map((side) => `${getDesignSideLabel(design, side)}: ${design.sides[side].text.trim()}`)
      .join('; ');
    if (textSummary) attributes.push({ key: 'Design text', value: trimAttribute(textSummary) });

    const textStyleSummary = designedSides
      .filter((side) => design.sides[side].text.trim())
      .map((side) => {
        const s = design.sides[side];
        const style = [
          getTextFontOption(s.textFont).label,
          s.textBold ? 'bold' : 'regular',
          s.textItalic ? 'italic' : '',
          getTextAlignOption(s.textAlign).label.toLowerCase(),
          `${round(s.textSize)}px`,
          s.textColor,
        ].filter(Boolean).join(', ');
        return `${getDesignSideLabel(design, side)}: ${style}`;
      })
      .join('; ');
    if (textStyleSummary) attributes.push({ key: 'Design text style', value: trimAttribute(textStyleSummary) });

    designedSides.forEach((side) => {
      attributes.push({
        key: side === 'front' ? '_designFront' : '_designBack',
        value: compactSideDesign(design.sides[side]),
      });
    });
  }

  if (design.instructions.trim()) {
    attributes.push({ key: 'Special instructions', value: trimAttribute(design.instructions) });
  }

  return attributes;
}
